import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Zap,
  DollarSign,
  User,
  LayoutGrid,
  Calendar,
  Clock,
  IdCard,
  Link
} from "lucide-react";
import DownloadButton from "../../components/DownloadButton";
import download from "downloadjs";
import axios from "axios";
import moment from "moment";
import _ from "lodash";
import { useMedia } from "../../hooks/useMedia";
import { floatToCurrency, base64 } from "../../constants/validate";

import ProposalHistory from "../../components/ProposalHistory";
import DebtInformation from "../../components/DebtInformation";
import SideBar from "./components/Sidebar";

import "./index.scss";
import Signature from "./components/Signatures";
import Table from "../../components/Table";
import { cnpjAlphanumeric as cnpjMask } from "../../constants/validate";
/* Fakedata accordions */
// const accordions = [
//   "Calculadora preditiva",
//   "Devedor",
//   "Garantidor",
//   "Informações adicionais",
//   "Comentários",
// ]

const tableHeaders = [
  "Sócio",
  "Recebido",
  "Abertura",
  "Cliques no link",
  // 'Acessos'
];

const tableObejctKeys = [
  "name",
  "received",
  "open_count",
  "click_count",
  // accesos?
];

const proposalsTableHeaders = ["Proposta", "Parcela", "Data"];

const proposalsTableObjectKeys = ["value", "number_installments", "created"];

const tableIcons = {
  Sócio: "",
  Recebimento: "Mail",
  Abertura: "Mail",
  "Cliques no link": "Mail",
};

function Case() {
  const { hash } = useParams();

  const dispatch = useDispatch();
  const {
    app: { user },
    crm: { cases, currentCase, keyword },
  } = useSelector((state) => state);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMedia("(max-width: 960px)");
  const [cnpj, setCnpj] = useState(currentCase?.case?.cnpj);
  const [caseMode, setCaseMode] = useState(1);

  const [filters, setFilters] = useState([
    { label: "Aguardando resposta", value: "SENT", active: false },
    { label: "Em negociação", value: "NEGOTIATION", active: false },
    { label: "Aguardando pagamento", value: "WAITING_PAYMENT", active: false },
    {
      label: "Aguardando assinatura",
      value: "WAITING_APPROVAL",
      active: false,
    },
    {
      label: "Aguardando assinatura e pagamento",
      value: "WAITING",
      active: false,
    },
    { label: "Concluído", value: "PAID", active: false },
    { label: "Arquivado", value: "ARCHIVED", active: false },
    { label: "Encerrado", value: "CLOSED", active: false },
  ]);
  const [search, setSearch] = useState(keyword);
  const [sortBy, setSortBy] = useState(["viewed_company", "DESC"]);
  const [searchIsActive, setSearchIsActive] = useState(false);
  const [infoIsOpen, setInfoIsOpen] = useState(false);
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [clickInCase, setClickInCase] = useState(false);
  const [isLoadingCases, setIsLoadingCases] = useState(true);
  const [isLoadingCase, setIsLoadingCase] = useState(true);
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);
  const [isLoadingMetricsExcel, setIsLoadingMetricsExcel] = useState(false);
  const newCaseId = location.state?.id;
  const [isOpenId, setIsOpenId] = useState(newCaseId);
  const [fetchAllCases, setFetchAllCases] = useState(true);
  const isFirstRender = useRef(true);
  const [proposalsHistory, setProposalsHistory] = useState([]);

  //region useCallbacks
  const setCases = useCallback(
    (data) => {
      // console.log(data)
      dispatch({ type: "ADD_CASES", payload: data });
    },
    [dispatch]
  );

  const setSearchResults = useCallback(
    (data) => {
      dispatch({ type: "ADD_SEARCH_RESULTS", payload: data });
    },
    [dispatch]
  );

  const setCurrentCase = useCallback(
    (data) => {
      dispatch({ type: "CHANGE_CURRENT_CASE", payload: data });
    },
    [dispatch]
  );

  const handleSelectCase = useCallback(
    async (caseId, caseController, isPing = false) => {
      if (!caseId) {
        setIsLoadingCase(false);
        setCurrentCase(null);
        return;
      }

      if (isMobile) {
        handleToggleSidebar();
      }

      if (!isPing) {
        setIsOpenId(caseId);
        setIsLoadingCase(true);
        setCurrentCase(null);
      }

      try {
        const caseResponse = await axios.get(`/case/${caseId}`, {
          signal: caseController && caseController.signal,
        });

        const commentsResponse = await axios.post(`/case-comment/list`, {
          cases_id: caseId,
        });
        // console.log(caseResponse.data)
        // await fetchChat(caseId, true, clickInCase)
        setCurrentCase(caseResponse.data);
        setClickInCase(false);
        const caseInfo = caseResponse.data.case;
        const formattedDebtData = {
          debt: caseInfo?.total_debts,
          id: caseInfo?.case_id,
          corporate: {
            name: caseInfo?.corporate_name,
            cnpj: caseInfo?.cnpj,
          },
          partner: {
            name: caseInfo?.name,
            email: caseInfo?.email,
            cpf: caseInfo?.cpf,
          },
          created: caseInfo?.created,
          openingDate: moment(caseInfo?.opening_date).format("DD/MM/YYYY"),
        };

        dispatch({ type: "RESET_PROPOSAL_HISTORY" });

        if (caseResponse.data.acceptedProposal) {
          dispatch({
            type: "CHANGE_PROPOSAL_HISTORY",
            payload: [caseResponse.data.acceptedProposal],
          });
        }

        let activeProposals = caseResponse.data.activeProposals;
        if (activeProposals.length > 0) {
          dispatch({
            type: "ADD_PROPOSAL_HISTORY",
            payload: activeProposals,
          });
        }

        let oldProposals = caseResponse.data.oldProposals;
        if (oldProposals.length > 0) {
          oldProposals = oldProposals.map(
            (archivedProposal) => archivedProposal
          );
        }

        let unpaidProposals = caseResponse.data.unpaidProposals;
        dispatch({ type: "CHANGE_OLD_PROPOSALS", payload: oldProposals });
        dispatch({ type: "CHANGE_UNPAID_PROPOSALS", payload: unpaidProposals });
        dispatch({ type: "CHANGE_DEBT_INFO", payload: formattedDebtData });

        dispatch({
          type: "CHANGE_CURRENT_CASE_COMMENTS",
          payload: commentsResponse.data,
        });
        setIsLoadingCase(false);
      } catch (error) {
        console.log(error);

        if (error.message !== "canceled") {
          setIsLoadingCase(false);
        }
        setCurrentCase(null);
      }
    },
    [dispatch, setCurrentCase, isMobile] //Before, had 'fetchChat'
  );

  const handleChangeSearch = useCallback((value) => {
    setSearch(value);
    setFetchAllCases(true);
  }, []);

  const handleCloseInfo = useCallback(() => {
    setInfoIsOpen(false);
  }, []);

  const handleDownloadExcelFinancial = useCallback(() => {
    setIsLoadingExcel(true);
    axios
      .get("/case/download-excel")
      .then((response) => {
        download(
          base64(response.data),
          `Casos Pagdeal (Financeiro).xlsx`,
          "application/x-www-form-urlencoded"
        );
        setIsLoadingExcel(false);
      })
      .catch((e) => {
        console.log(e);
        setIsLoadingExcel(false);
      });
  }, []);

  const handleDownloadExcelJudicial = useCallback(() => {
    setIsLoadingExcel(true);
    axios
      .get("/case/download-excel-judicial")
      .then((response) => {
        download(
          base64(response.data),
          `Casos Pagdeal (Judicial).xlsx`,
          "application/x-www-form-urlencoded"
        );
        setIsLoadingExcel(false);
      })
      .catch((e) => {
        console.log(e);
        setIsLoadingExcel(false);
      });
  }, []);

  const handleDownloadExcel = {
    financial: handleDownloadExcelFinancial,
    judicial: handleDownloadExcelJudicial,
  };

  const handleMetricsExcel = useCallback(() => {
    setIsLoadingMetricsExcel(true);
    axios
      .get("/case/download-invite-excel")
      .then((response) => {
        download(
          base64(response.data),
          `Métricas Pagdeal.xlsx`,
          "application/x-www-form-urlencoded"
        );
        setIsLoadingMetricsExcel(false);
      })
      .catch((e) => {
        console.log(e);
      });
  }, []);
  //endregion

  useEffect(() => {
    if (currentCase?.case?.cnpj != undefined) {
      console.log("-----> ", currentCase?.case?.cnpj);
      setCnpj(cnpjMask(currentCase?.case?.cnpj));
    }
    // if (cnpj != undefined)
    //setCnpj(cnpjMask(currentCase?.case?.cnpj));
  }, [currentCase?.case?.cnpj]);

  useEffect(() => {
    const caseController = new AbortController();
    if (clickInCase) {
      handleSelectCase(isOpenId, caseController);
    }

    return () => {
      caseController.abort();
    };

    // eslint-disable-next-line
  }, [clickInCase, isOpenId]);

  useEffect(() => {
    if (currentCase?.case?.cnpj) {
      fetchProposalsHistory(currentCase.case.cnpj).then(setProposalsHistory);
    }
  }, [currentCase?.case?.cnpj]);

  async function reloadProposal(cnpj) {
    await fetchProposalsHistory(cnpj).then(setProposalsHistory);
  }

  async function fetchProposalsHistory(cnpj) {
    try {
      let url = "/case/proposals-history";
      if (caseMode === 2) url = "/case/proposals-history-chat";
      const response = await axios.post(url, { cnpj });
      const proposalsHistory = response.data;
      console.log(response.data);
      const formattedProposals = proposalsHistory.map((proposal) => {
        return {
          ...proposal,
          created: formatDate(proposal.created),
        };
      });

      return formattedProposals;
    } catch (e) {
      console.error("Error fetching proposals history:", e);
      return [];
    }
  }

  function formatDate(isoDate) {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  async function fetchCases() {
    setIsLoadingCases(true);
    try {
      const filtersList = {};
      filters.forEach((filter) => (filtersList[filter.value] = filter.active));
      const response = await axios.post("/case/list", {
        filter: {
          search,
          status: filtersList,
          sortBy,
        },
      });

      dispatch({ type: "RESET_CASES" });
      dispatch({ type: "RESET_SEARCH_RESULTS" });
      if (!Array.isArray(response.data)) {
        setSearchResults(response.data);
        setSearchIsActive(true);
      } else {
        setSearchResults({});
        setSearchIsActive(false);
      }

      if (response.data.length > 0) {
        const formattedCases = response.data.map((currentCase) => ({
          caseId: currentCase?.case_id,
          clientName: currentCase.corporate_name,
          createdAt: currentCase.created,
          viewed: currentCase.viewed_company === 0 ? false : true,
          status: currentCase.status,
          typeId: currentCase.cases_types_id,
        }));

        setCases(formattedCases);
        setSearchIsActive(false);

        if (isFirstRender.current) {
          navigate(location.pathname, {});
          if (isOpenId) {
            handleSelectCase(isOpenId);
          } else {
            handleSelectCase(formattedCases[0].caseId);
          }
        }
      } else {
        setCases([]);
        setIsLoadingCase(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoadingCase(false);
    } finally {
      setIsLoadingCases(false);
      isFirstRender.current = false;
    }
  }

  useEffect(() => {
    if (!_.isEmpty(user) && fetchAllCases) {
      fetchCases();
      setFetchAllCases(false);
    }
  }, [
    filters,
    search,
    sortBy,
    user,
    navigate,
    location.pathname,
    fetchAllCases,
    isOpenId,
    setSearchResults,
    dispatch,
    setCases,
    handleSelectCase,
  ]);

  useEffect(() => {
    if (!currentCase) return;
    // const caseController = new AbortController();

    const timer = 10 * 1000;
    const interval = setInterval(async () => {
      await handleSelectCase(currentCase?.case?.case_id, null, true);
    }, timer);

    return () => {
      clearInterval(interval);
    };
  }, [currentCase, handleSelectCase]);

  useEffect(() => {
    if (caseMode === 1) {
      setFilters([
        { label: "Aguardando resposta", value: "SENT", active: false },
        { label: "Em negociação", value: "NEGOTIATION", active: false },
        {
          label: "Aguardando pagamento",
          value: "WAITING_PAYMENT",
          active: false,
        },
        {
          label: "Aguardando assinatura",
          value: "WAITING_APPROVAL",
          active: false,
        },
        {
          label: "Aguardando assinatura e pagamento",
          value: "WAITING",
          active: false,
        },
        { label: "Concluído", value: "PAID", active: false },
        { label: "Arquivado", value: "ARCHIVED", active: false },
        { label: "Encerrado", value: "CLOSED", active: false },
      ]);
    } else {
      setFilters([
        {
          label: "Negociando por chat",
          value: "CHAT NEGOTIATION",
          active: false,
        },
        { label: "Aguardando contato", value: "SENT_CHAT", active: false },
        {
          label: "Aguardando assinaturas",
          value: "WAITING_APPROVAL_CHAT",
          active: false,
        },
        { label: "Concluido", value: "PAID_CHAT", active: false },
        {
          label: "Aguardando pagamento",
          value: "WAITING_PAYMENT_CHAT",
          active: false,
        },
        {
          label: "Aguardando assinatura e pagamento",
          value: "WAITING_CHAT",
          active: false,
        },
        { label: "Arquivado", value: "ARCHIVED_CHAT", active: false },
        { label: "Encerrado", value: "CLOSED_CHAT", active: false },
      ]);
    }
  }, [caseMode]);

  useEffect(() => {
    if (filters.length > 0) {
      fetchCases();
    }
  }, [filters]);

  //#region handles
  function handleToggleInfo() {
    setInfoIsOpen((prev) => !prev);
  }

  function handleToggleSidebar() {
    setSidebarIsOpen((prev) => !prev);
  }

  function handleCloseSidebar() {
    setSidebarIsOpen(false);
  }

  function handleChangeFilters(filterName) {
    // Select only one filter
    // const newFilters = filters.map((currentFilter) => {
    //   const isActive = currentFilter.label === filterName
    //   return { ...currentFilter, active: isActive }
    // })

    // Select one or more filters
    const newFilters = [...filters];
    newFilters.find((f) => f.label === filterName).active = !newFilters.find(
      (f) => f.label === filterName
    ).active;
    setFilters(newFilters);
    setFetchAllCases(true);
  }

  function handleSortBy(value) {
    setSortBy(value);
    setFetchAllCases(true);
  }
  //#endregion

  //#region methods
  function checkValue(value, type) {
    if (!value) return "--";

    let formattedValue = null;

    switch (type) {
      case "currency":
        formattedValue = floatToCurrency(Number(value));
        break;
      case "date":
        formattedValue = moment(value).format("DD/MM/YYYY");
        break;
      case "text":
        formattedValue = value;
        break;
      default:
        break;
    }

    return formattedValue;
  }

  function manyDays(date) {
    let days = moment(date).diff(new Date(), "days");
    days += 1;
    return "Faltam " + days + (days > 1 ? " dias" : "dia");
  }

  function onSelectCase(caseId) {
    setClickInCase(true);
    setIsOpenId(caseId);
  }

  function returnClassNameSidebar() {
    let className = "case__sidebar";

    if (sidebarIsOpen) className += " case__sidebar--open";

    return className;
  }

  function returnClassNameProposalHistory() {
    let className = "case__wrapper-history";

    if (!sidebarIsOpen && !infoIsOpen)
      className += " case__wrapper-history--open";

    return className;
  }

  function returnClassNameWrapperInfo() {
    let className = "case__wrapper-info";

    if (infoIsOpen) className += " case__wrapper-info--open";

    return className;
  }

  function formatInvites(invites) {
    if (!invites) return [];

    let formattedInvites = [];

    invites.forEach((invite) => {
      let formattedInvite = { ...invite };
      formattedInvite.received = invite.received === 1 ? "Sim" : "Não";
      formattedInvites.push(formattedInvite);
    });

    return formattedInvites;
  }
  //#endregion
  // console.log(currentCase?.case?.cnpj)

  const getTopics = (currentCase) => {
    if (currentCase?.case?.cases_types_id === 2) {
      const base = [
        {
          Icon: IdCard,
          title: "CNPJ",
          value: cnpj,
          hasSubtopics: false,
        },
        {
          Icon: LayoutGrid,
          title: "N° do processo",
          value: checkValue(currentCase?.case?.process_number, "text"),
          hasSubtopics: false,
        },
        {
          Icon: User,
          title: "Sócio Administrador",
          value: checkValue(currentCase?.case?.name, "text"),
          hasSubtopics: false,
        },
        {
          Icon: Clock,
          title: "Caso criado em",
          value: checkValue(currentCase?.case?.created, "date"),
          hasSubtopics: false,
        },
      ];
      if (currentCase?.case?.signature_url) {
        base.push({
          Icon: Link,
          title: "Link de assinatura",
          value: currentCase.case.signature_url,
          type: "link",
          hasSubtopics: false,
        });
      }
      return base;
    } else {
      const base = [
        {
          Icon: IdCard,
          title: "CNPJ",
          value: cnpj,
          hasSubtopics: false,
        },
        {
          Icon: DollarSign,
          title: "Valor histórico",
          value: checkValue(currentCase?.case?.total_debts, "currency"),
          hasSubtopics: false,
        },
        {
          Icon: Zap,
          title: "Valor total atualizado",
          value: checkValue(currentCase?.refreshedValue, "currency"),
          hasSubtopics: false,
        },
        {
          Icon: User,
          title: "Sócio Administrador",
          value: checkValue(currentCase?.case?.name, "text"),
          hasSubtopics: false,
        },
        {
          Icon: Clock,
          title: "Caso criado em",
          value: checkValue(currentCase?.case?.created, "date"),
          hasSubtopics: false,
        },
      ];
      if (currentCase?.case?.signature_url) {
        base.push({
          Icon: Link,
          title: "Link de assinatura",
          value: currentCase.case.signature_url,
          type: "link",
          hasSubtopics: false,
        });
      }
      return base;
    }
  };

  const topics = getTopics(currentCase);

  //  console.log(currentCase?.case?.status)
  if (
    currentCase?.case?.status !== "ARCHIVED" &&
    currentCase?.case?.status !== "SENT" &&
    currentCase?.case?.cases_types_id !== 2
  ) {
    let calculation_date = moment(currentCase?.case?.calculation_date).add(
      -1,
      "days"
    );

    topics.push({
      Icon: Calendar,
      title: "Caso expira em",
      value: checkValue(calculation_date, "date"),
      hasSubtopics: false,
    });
    // topics.push({
    //   Icon: Folder,
    //   title: "",
    //   value: manyDays(calculation_date),
    //   hasSubtopics: false,
    // })
  }

  useEffect(() => {
    console.log(hash);

    if (hash != undefined) {
      onSelectCase(hash);
    }
  }, [hash]);

  const accordions = [];

  if (
    currentCase?.signatures?.length > 0 &&
    currentCase?.case?.status !== "ARCHIVED"
  ) {
    accordions.push({
      title: "Minuta",
      key: "SIGNATURES",
      icon: "File",
      component: <Signature />,
    });
  }

  accordions.push({
    title: "Propostas",
    key: "PROPOSALS",
    component: (
      <div style={{ marginTop: "10px" }}>
        <Table
          headers={proposalsTableHeaders}
          objectKeys={proposalsTableObjectKeys}
          items={proposalsHistory}
          whiteSpace="nowrap"
          tableIcons={tableIcons}
          hasScroll={true}
          collapseColumns={false}
        />
      </div>
    ),
  });

  // accordions.push({
  //   title: "Métricas",
  //   key: "METRICS",
  //   component: (
  //     <div style={{ marginTop: "10px" }}>
  //       <Table
  //         headers={tableHeaders}
  //         objectKeys={tableObejctKeys}
  //         items={formatInvites(currentCase?.invites)}
  //         whiteSpace="nowrap"
  //         tableIcons={tableIcons}
  //         hasScroll={true}
  //         collapseColumns={false}
  //       />
  //     </div>
  //   ),
  // });

  return (
    <div className="case">
      <aside className={returnClassNameSidebar()}>
        <SideBar
          isOpenId={isOpenId}
          handleSelectCase={onSelectCase}
          searchIsActive={searchIsActive}
          filters={filters}
          isLoading={isLoadingCases}
          setOrder={handleSortBy}
          handleClose={handleCloseSidebar}
          handleChangeFilters={handleChangeFilters}
          handleChangeSearch={handleChangeSearch}
          handleDownloadExcel={handleDownloadExcel}
          isLoadingExcel={isLoadingExcel}
          handleMetricsExcel={handleMetricsExcel}
          isLoadingMetricsExcel={isLoadingMetricsExcel}
          caseMode={caseMode}
          setCaseMode={setCaseMode}
        />
      </aside>

      <main style={{ width: sidebarIsOpen ? "unset" : "100%" }}>
        <div className={returnClassNameProposalHistory()}>
          <ProposalHistory
            withListButton={isMobile}
            withShareButton={isMobile}
            withInfoButton={isMobile}
            handleShowInfo={handleToggleInfo}
            handleShowSidebar={handleToggleSidebar}
            initiator="other"
            type="operator"
            isLoading={isLoadingCase}
            withoutCases={!isOpenId && _.isEmpty(cases)}
            reloadProposal={reloadProposal}
            fetchCases={fetchCases}
          />
        </div>

        <div className={returnClassNameWrapperInfo()}>
          <DebtInformation
            title="Detalhes"
            topics={topics}
            accordions={accordions}
            hasCloseIcon
            handleClose={handleCloseInfo}
            isLoading={isLoadingCase}
          />
        </div>
      </main>
    </div>
  );
}

export default Case;
